import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import {Location} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationService,ModalService, PatientService, ValidateService} from '../../services';
import {BrightBlueFormService} from '../../services/bright-blue-form.service';
import {Errors} from '../../models';

export interface Project {
  id: number;
  project_name: string;
  purchaser: string;
  place: string;
  contract_start?: Date;
  contract_end?: Date;
  assigned_caregivers?: number;
}

@Component({
  selector: 'app-right-project-panel',
  templateUrl: './right-project-panel.component.html',
  styleUrls: ['./right-project-panel.component.scss']
})
export class RightProjectPanelComponent implements OnInit {
  @Input() project: Project;
  @Output() closePanelEvent = new EventEmitter<void>();
  @Output() saveProjectEvent = new EventEmitter<Project>();
  @Output() projectUpdated = new EventEmitter<void>();

  public projectForm: FormGroup;
  public errors: Errors = {errors: {}};
  public isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    public location: Location,
    private translate: TranslateService,
    private modalService: ModalService,
    private confirmation: ConfirmationService,
    private patientService: PatientService,
    private validateService: ValidateService,
  ) { }

  ngOnInit() {
    this.projectForm = new FormGroup({
      id: new FormControl(this.project?.id || null),
      project_name: new FormControl(this.project?.project_name || '', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.pattern(/^[_A-Za-z0-9][_A-Za-z0-9 -]*$/),
        this.validateService.checkLengthName
      ]),
      purchaser: new FormControl(this.project?.purchaser || '', [
        Validators.required,
        Validators.maxLength(255)
      ]),
      place: new FormControl(this.project?.place || '', [
        Validators.maxLength(255)
      ]),
      contract_start: new FormControl(this.convertDateToPickerFormat(this.project?.contract_start)),
      contract_end: new FormControl(this.convertDateToPickerFormat(this.project?.contract_end))
    });
  }

  private convertDateToPickerFormat(date: any): any {
    if (!date) {
      return null;
    }

    let dateObj: Date;

    if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      if (date.year && date.month && date.day) {
        return date;
      }
      return null;
    }

    if (isNaN(dateObj.getTime())) {
      return null;
    }

    return {
      year: dateObj.getFullYear(),
      month: dateObj.getMonth() + 1,
      day: dateObj.getDate()
    };
  }

  private formatNgbDate(value: any): string {
    if (value && typeof value === 'object' && value.year) {
      return `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
    }
    return '';
  }

  closePanel() {
    this.closePanelEvent.emit();
  }

  saveProject() {
    const startVal = this.projectForm.get('contract_start').value;
    const endVal = this.projectForm.get('contract_end').value;

    if (startVal && endVal && startVal.year && endVal.year) {
      const startDate = new Date(startVal.year, startVal.month - 1, startVal.day);
      const endDate = new Date(endVal.year, endVal.month - 1, endVal.day);
      if (endDate < startDate) {
        this.modalService.open(this.translate.instant('PROJECTS.END_DATE_BEFORE_START'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.PROJECTS-END_DATE_BEFORE_START');
        return;
      }
    }

    if (this.projectForm.valid) {
      this.modalService.openAccept(this.translate.instant('BACKEND.BEACON-CHECK'), 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.BACKEND-BEACON-CHECK')
        .subscribe(
          accept => {
            if (accept.ok === true) {
              this.isSubmitting = true;
              this.errors = {errors: {}};

              const getDateStartFormat = this.formatNgbDate(this.projectForm.get('contract_start').value);
              const getDateEndFormat = this.formatNgbDate(this.projectForm.get('contract_end').value);

              this.patientService.editProject(
                this.projectForm.get('id').value,
                this.projectForm.get('project_name').value,
                this.projectForm.get('purchaser').value,
                this.projectForm.get('place').value,
                getDateStartFormat,
                getDateEndFormat
              ).subscribe(
                () => {
                  this.closePanel();
                  this.confirmation.show('PROJECT.PROJECT-EDITED', { name: this.projectForm.get('project_name').value }, 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.PROJECT-PROJECT-EDITED');
                  this.projectUpdated.emit();
                },
                err => {
                  this.errors = err;
                  this.isSubmitting = false;
                },
                () => this.isSubmitting = false
              );
            }
          }
        );
    } else {
      this.validateService.validateAllFormFields(this.projectForm);
      this.modalService.open(this.translate.instant('BACKEND.ALL_FIELDS_REQUIRED'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.BACKEND-ALL_FIELDS_REQUIRED');
    }
  }

  onButtonClick(type: string) {
    if (type === 'secondary') {
      this.closePanel();
    }
  }

  onKeyDown(event: KeyboardEvent, type: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onButtonClick(type);
    }
  }

  deleteProject() {
    this.modalService
      .openAccept(this.translate.instant('PROJECT.PROJECT-ASK-DELETE', { name: this.projectForm.get('project_name').value }), 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.PROJECT-PROJECT-ASK-DELETE')
      .subscribe(data => {
        if (data.ok) {
          this.patientService.deleteProject(this.projectForm.get('id').value).subscribe(
            () => {
              this.confirmation.show('PROJECT.PROJECT-DELETED', undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.PROJECT-PROJECT-DELETED');
              this.closePanel();
              this.projectUpdated.emit();
            },
            () => this.modalService.open(this.translate.instant('BACKEND.COULDNT-DELETE'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-PROJECT-PANEL.POPUP.BACKEND-COULDNT-DELETE')
          );
        }
      });
  }

}
